import React, { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { jsPDF } from 'jspdf';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Footer from '@/components/Footer';
import { useSEO } from '@/hooks/useSEO';
import { Download, ShieldCheck, ChevronDown, QrCode } from 'lucide-react';

/*
 * Swiss QR-Rechnung Generator (SEO-Auftrag Block 5.1, Aug 2026).
 * Nach Swiss Payment Standards / SIX Implementation Guidelines v2.3 (ISO 20022):
 * SPC-Payload Version 0200, Fehlerkorrektur M, Schweizerkreuz im Zentrum,
 * Zahlteil (148×105 mm) + Empfangsschein (62×105 mm) auf A4 unten.
 * 100 % clientseitig — keine Zahlungsdaten verlassen den Browser.
 */

// ---------- Validierung ----------
const mod97 = (s) => {
  let r = 0;
  for (const ch of s) r = (r * 10 + parseInt(ch, 10)) % 97;
  return r;
};
const ibanToDigits = (iban) =>
  (iban.slice(4) + iban.slice(0, 4)).split('').map((c) => (/[A-Z]/.test(c) ? c.charCodeAt(0) - 55 : c)).join('');
const cleanIban = (v) => v.replace(/\s+/g, '').toUpperCase();
const isValidIban = (v) => {
  const iban = cleanIban(v);
  if (!/^(CH|LI)\d{19}$/.test(iban)) return false;
  return mod97(ibanToDigits(iban)) === 1;
};
const isQrIban = (v) => {
  const iban = cleanIban(v);
  const iid = parseInt(iban.slice(4, 9), 10);
  return iid >= 30000 && iid <= 31999;
};
// QR-Referenz: 27 Stellen, Prüfziffer rekursiv Modulo 10
const MOD10_TABLE = [0, 9, 4, 6, 8, 2, 7, 1, 3, 5];
const qrRefCheckDigit = (ref26) => {
  let carry = 0;
  for (const c of ref26) carry = MOD10_TABLE[(carry + parseInt(c, 10)) % 10];
  return (10 - carry) % 10;
};
const isValidQrRef = (v) => {
  const r = v.replace(/\s+/g, '');
  return /^\d{27}$/.test(r) && qrRefCheckDigit(r.slice(0, 26)) === parseInt(r[26], 10);
};
const isValidScor = (v) => {
  const r = v.replace(/\s+/g, '').toUpperCase();
  if (!/^RF\d{2}[A-Z0-9]{1,21}$/.test(r)) return false;
  const digits = (r.slice(4) + r.slice(0, 4)).split('').map((c) => (/[A-Z]/.test(c) ? c.charCodeAt(0) - 55 : c)).join('');
  return mod97(digits) === 1;
};
const fmtRef = (r) => r.replace(/\s+/g, '').replace(/(.{5})/g, '$1 ').trim();
const fmtIban = (i) => cleanIban(i).replace(/(.{4})/g, '$1 ').trim();
const fmtAmount = (a) => Number(a).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/,/g, "'");

// ---------- SPC-Payload (SIX v2.x) ----------
const buildPayload = (d) => {
  const refType = d.refType;
  const lines = [
    'SPC', '0200', '1',
    cleanIban(d.iban),
    'K', d.crName.trim(), d.crStreet.trim(), `${d.crZip.trim()} ${d.crCity.trim()}`.trim(), '', '', 'CH',
    '', '', '', '', '', '', '',
    d.amount ? Number(d.amount).toFixed(2) : '',
    d.currency,
  ];
  if (d.dbName.trim()) {
    lines.push('K', d.dbName.trim(), d.dbStreet.trim(), `${d.dbZip.trim()} ${d.dbCity.trim()}`.trim(), '', '', 'CH');
  } else {
    lines.push('', '', '', '', '', '', '');
  }
  lines.push(refType, refType === 'NON' ? '' : d.reference.replace(/\s+/g, '').toUpperCase());
  lines.push(d.message.trim(), 'EPD');
  return lines.join('\n');
};

const FAQ = [
  { q: 'Was ist eine QR-Rechnung?', a: 'Die QR-Rechnung ist seit Oktober 2022 der Standard für Rechnungen im Schweizer Zahlungsverkehr. Sie ersetzt die alten roten und orangen Einzahlungsscheine. Der Swiss QR Code enthält alle Zahlungsinformationen — Empfänger, IBAN, Betrag und Referenz — und kann mit jeder Banking-App gescannt werden.' },
  { q: 'Was ist der Unterschied zwischen IBAN und QR-IBAN?', a: 'Die QR-IBAN erkennt man an der Instituts-Identifikation 30000–31999 (Stellen 5–9). Sie wird ausschliesslich mit einer 27-stelligen QR-Referenz verwendet. Eine normale IBAN funktioniert mit Creditor Reference (RF…) oder ganz ohne Referenz. Der Generator erkennt den Typ automatisch.' },
  { q: 'Ist der Generator wirklich kostenlos?', a: 'Ja — ohne Anmeldung, ohne E-Mail-Angabe, ohne Wasserzeichen. Ihre Zahlungsdaten werden ausschliesslich in Ihrem Browser verarbeitet und nie an einen Server gesendet.' },
  { q: 'Welche MWST-Sätze gelten in der Schweiz?', a: 'Seit 2024: 8,1 % Normalsatz, 2,6 % reduzierter Satz (z. B. Lebensmittel) und 3,8 % Sondersatz für Beherbergung. MWST-pflichtig sind Selbständigerwerbende ab CHF 100\'000 Jahresumsatz.' },
  { q: 'Welche Pflichtangaben braucht eine Rechnung in der Schweiz?', a: 'Nach Art. 26 MWSTG: Name und Ort von Leistungserbringer und Empfänger, MWST-Nummer (UID mit Zusatz MWST) des Erbringers, Datum bzw. Leistungszeitraum, Art und Umfang der Leistung, Entgelt sowie Steuersatz und Steuerbetrag (oder der Hinweis, dass die MWST im Preis enthalten ist). Nicht MWST-pflichtige Selbständige lassen den Steuerausweis weg.' },
  { q: 'Wie lange müssen Rechnungen in der Schweiz aufbewahrt werden?', a: 'Geschäftsbücher und Belege sind nach Art. 958f OR zehn Jahre aufzubewahren. Für die MWST gilt zusätzlich Art. 70 MWSTG (ebenfalls 10 Jahre, bei Immobilien 20 Jahre). Ein PDF im Archiv genügt, wenn Integrität und Lesbarkeit sichergestellt sind.' },
  { q: 'Muss ich als Freelancer in der Schweiz MWST auf die Rechnung schreiben?', a: 'Erst ab CHF 100\'000 steuerbarem Jahresumsatz (Art. 10 MWSTG) bist du obligatorisch MWST-pflichtig – oder wenn du dich freiwillig registrierst. Darunter stellst du Rechnungen ohne MWST-Ausweis; der Swiss QR Code funktioniert unabhängig davon.' },
  { q: 'Erfüllt das PDF die Vorgaben von SIX?', a: 'Der Zahlteil (148 × 105 mm) und der Empfangsschein (62 × 105 mm) folgen dem Layout der Swiss Implementation Guidelines: Swiss QR Code 46 × 46 mm mit Schweizerkreuz, Perforationslinien mit Schere-Symbol und alle Pflichtelemente.' },
];

export default function QrRechnungGenerator() {
  const [form, setForm] = useState({
    crName: '', crStreet: '', crZip: '', crCity: '', iban: '',
    dbName: '', dbStreet: '', dbZip: '', dbCity: '',
    amount: '', currency: 'CHF', reference: '', message: '',
  });
  const [refChoice, setRefChoice] = useState('AUTO'); // AUTO | SCOR | NON
  const [openFaq, setOpenFaq] = useState(null);
  const [errors, setErrors] = useState({});
  const qrWrapRef = useRef(null);

  useSEO({
    title: 'Rechnung erstellen Schweiz – QR-Rechnung kostenlos',
    description: 'Rechnung mit Swiss QR-Bill in 2 Minuten: Zahlteil & Empfangsschein nach SIX, QR-IBAN, Referenz, MWST 8,1 %. Kostenlos, ohne Anmeldung.',
    canonical: 'https://ivoliq.io/qr-rechnung-erstellen-kostenlos',
    robots: 'index,follow',
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const qrIban = form.iban && isValidIban(form.iban) && isQrIban(form.iban);
  const refType = qrIban ? 'QRR' : refChoice === 'SCOR' ? 'SCOR' : 'NON';

  const validate = () => {
    const e = {};
    if (!form.crName.trim()) e.crName = 'Name erforderlich';
    if (!form.crZip.trim() || !form.crCity.trim()) e.crCity = 'PLZ und Ort erforderlich';
    if (!isValidIban(form.iban)) e.iban = 'Ungültige CH/LI-IBAN (Prüfziffer)';
    if (refType === 'QRR' && !isValidQrRef(form.reference)) e.reference = 'QR-IBAN erfordert gültige 27-stellige QR-Referenz';
    if (refType === 'SCOR' && !isValidScor(form.reference)) e.reference = 'Ungültige Creditor Reference (RF…)';
    if (form.amount && (isNaN(Number(form.amount)) || Number(form.amount) <= 0 || Number(form.amount) > 999999999.99)) e.amount = 'Ungültiger Betrag';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const payload = useMemo(() => {
    if (!isValidIban(form.iban) || !form.crName.trim()) return null;
    return buildPayload({ ...form, refType });
  }, [form, refType]);

  const downloadPdf = () => {
    if (!validate() || !payload) return;
    const canvas = qrWrapRef.current?.querySelector('canvas');
    if (!canvas) return;
    const qrImg = canvas.toDataURL('image/png');

    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const top = 297 - 105; // Zahlteil unten auf A4

    // Trennlinien mit Schere
    doc.setLineDashPattern([1, 1], 0);
    doc.setDrawColor(0);
    doc.line(0, top, 210, top);
    doc.line(62, top, 62, 297);
    doc.setLineDashPattern([], 0);
    doc.setFontSize(8);
    doc.text('✂', 5, top - 1);

    const H = (t, x, y, size = 6) => { doc.setFont('helvetica', 'bold'); doc.setFontSize(size); doc.text(t, x, y); };
    const V = (t, x, y, size = 8, maxW = 52) => { doc.setFont('helvetica', 'normal'); doc.setFontSize(size); doc.text(doc.splitTextToSize(t, maxW), x, y); };

    // ----- Empfangsschein (links, 62 mm) -----
    let x = 5, y = top + 10;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.text('Empfangsschein', x, y);
    y += 7; H('Konto / Zahlbar an', x, y); y += 3.5;
    V(`${fmtIban(form.iban)}\n${form.crName}\n${form.crStreet}\n${form.crZip} ${form.crCity}`, x, y); y += 16;
    if (refType !== 'NON') { H('Referenz', x, y); y += 3.5; V(fmtRef(form.reference), x, y); y += 7; }
    H('Zahlbar durch' + (form.dbName ? '' : ' (Name/Adresse)'), x, y); y += 3.5;
    if (form.dbName) V(`${form.dbName}\n${form.dbStreet}\n${form.dbZip} ${form.dbCity}`, x, y);
    else doc.rect(x, y, 52, 20);
    // Währung/Betrag
    H('Währung', x, top + 70); H('Betrag', x + 15, top + 70);
    V(form.currency, x, top + 74); 
    if (form.amount) V(fmtAmount(form.amount), x + 15, top + 74);
    else doc.rect(x + 15, top + 71, 30, 8);
    H('Annahmestelle', x + 32, top + 85);

    // ----- Zahlteil (rechts, 148 mm) -----
    x = 67; y = top + 10;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.text('Zahlteil', x, y);
    // QR-Code 46×46 mm
    doc.addImage(qrImg, 'PNG', 67, top + 17, 46, 46);
    // Schweizerkreuz 7×7 mm zentriert
    const cx = 67 + 23, cy = top + 17 + 23;
    doc.setFillColor(0, 0, 0); doc.rect(cx - 3.5, cy - 3.5, 7, 7, 'F');
    doc.setFillColor(255, 255, 255);
    doc.rect(cx - 0.6, cy - 2.4, 1.2, 4.8, 'F');
    doc.rect(cx - 2.4, cy - 0.6, 4.8, 1.2, 'F');
    // Währung/Betrag unter QR
    H('Währung', 67, top + 72, 7); H('Betrag', 67 + 20, top + 72, 7);
    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    doc.text(form.currency, 67, top + 77);
    if (form.amount) doc.text(fmtAmount(form.amount), 67 + 20, top + 77);
    else doc.rect(67 + 20, top + 73, 35, 9);
    // Rechte Spalte
    x = 118; y = top + 10;
    H('Konto / Zahlbar an', x, y, 7); y += 4;
    V(`${fmtIban(form.iban)}\n${form.crName}\n${form.crStreet}\n${form.crZip} ${form.crCity}`, x, y, 9, 85); y += 18;
    if (refType !== 'NON') { H('Referenz', x, y, 7); y += 4; V(fmtRef(form.reference), x, y, 9, 85); y += 8; }
    if (form.message.trim()) { H('Zusätzliche Informationen', x, y, 7); y += 4; V(form.message, x, y, 9, 85); y += 10; }
    H('Zahlbar durch' + (form.dbName ? '' : ' (Name/Adresse)'), x, y, 7); y += 4;
    if (form.dbName) V(`${form.dbName}\n${form.dbStreet}\n${form.dbZip} ${form.dbCity}`, x, y, 9, 85);
    else doc.rect(x, y, 65, 22);

    doc.save('qr-rechnung.pdf');
  };

  const faqSchema = {
    '@context': 'https://schema.org', '@type': 'FAQPage',
    mainEntity: FAQ.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  };

  const field = (label, key, props = {}) => (
    <div>
      <Label className="text-sm">{label}</Label>
      <Input value={form[key]} onChange={set(key)} data-testid={`qr-input-${key}`} {...props} />
      {errors[key] && <p className="text-xs text-red-600 mt-1">{errors[key]}</p>}
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org', '@type': 'SoftwareApplication', name: 'ivoliq QR-Rechnungs-Generator',
        applicationCategory: 'FinanceApplication', operatingSystem: 'Web', url: 'https://ivoliq.io/qr-rechnung-erstellen-kostenlos',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'CHF' },
      }) }} />

      {/* Hero */}
      <section className="bg-gradient-to-b from-emerald-50 to-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-5">
            Rechnung erstellen Schweiz — <span className="text-emerald-600">mit Swiss QR-Bill, kostenlos</span>
          </h1>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            Swiss QR-Code Rechnung in 2 Minuten: Zahlteil und Empfangsschein nach SIX-Vorgaben, als PDF direkt herunterladen.
            Mit QR-IBAN, QR-Referenz oder Creditor Reference. Ihre Daten bleiben im Browser.
          </p>
          <div className="flex items-center justify-center gap-6 mt-6 text-sm text-gray-500">
            <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Keine Datenübertragung</span>
            <span className="flex items-center gap-1.5"><Download className="h-4 w-4 text-emerald-600" /> PDF sofort</span>
            <span className="flex items-center gap-1.5"><QrCode className="h-4 w-4 text-emerald-600" /> ISO 20022</span>
          </div>
        </div>
      </section>

      {/* Generator */}
      <section className="py-12" data-testid="qr-generator">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1.2fr_0.8fr] gap-10">
          <div className="space-y-6">
            <div className="rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Zahlungsempfänger</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {field('Name / Firma *', 'crName')}
                {field('Strasse & Nr.', 'crStreet')}
                {field('PLZ *', 'crZip')}
                {field('Ort *', 'crCity')}
                <div className="sm:col-span-2">
                  {field('IBAN oder QR-IBAN (CH/LI) *', 'iban', { placeholder: 'CH44 3199 9123 0008 8901 2' })}
                  {form.iban && isValidIban(form.iban) && (
                    <p className="text-xs mt-1 text-emerald-700">{qrIban ? '✓ QR-IBAN erkannt → QR-Referenz erforderlich' : '✓ Gültige IBAN'}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Referenz & Betrag</h2>
              {!qrIban && (
                <div className="flex gap-2 mb-4">
                  {[['NON', 'Ohne Referenz'], ['SCOR', 'Creditor Reference (RF…)']].map(([v, l]) => (
                    <button key={v} type="button" onClick={() => setRefChoice(v)} data-testid={`qr-reftype-${v}`}
                      className={`px-3 py-1.5 rounded-full text-sm border ${refChoice === v || (refChoice === 'AUTO' && v === 'NON') ? 'bg-emerald-600 text-white border-emerald-600' : 'border-gray-300 text-gray-600'}`}>
                      {l}
                    </button>
                  ))}
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-4">
                {(qrIban || refChoice === 'SCOR') && (
                  <div className="sm:col-span-2">
                    {field(qrIban ? 'QR-Referenz (27 Stellen) *' : 'Creditor Reference (SCOR) *', 'reference', { placeholder: qrIban ? '21 00000 00003 13947 14300 09017' : 'RF18 5390 0754 7034' })}
                  </div>
                )}
                {field('Betrag (leer = offen)', 'amount', { placeholder: '1250.00', inputMode: 'decimal' })}
                <div>
                  <Label className="text-sm">Währung</Label>
                  <div className="flex gap-2 mt-1">
                    {['CHF', 'EUR'].map((c) => (
                      <button key={c} type="button" data-testid={`qr-currency-${c}`} onClick={() => setForm((f) => ({ ...f, currency: c }))}
                        className={`px-4 py-2 rounded-lg text-sm border ${form.currency === c ? 'bg-emerald-600 text-white border-emerald-600' : 'border-gray-300 text-gray-600'}`}>{c}</button>
                    ))}
                  </div>
                </div>
                <div className="sm:col-span-2">{field('Zusätzliche Informationen (z. B. Rechnungsnummer)', 'message', { maxLength: 140 })}</div>
              </div>
              <p className="text-xs text-gray-500 mt-3">Schweizer MWST-Sätze zur Orientierung: 8,1 % Normalsatz · 2,6 % reduziert · 3,8 % Beherbergung.</p>
            </div>

            <div className="rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Zahlungspflichtiger (optional)</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {field('Name / Firma', 'dbName')}
                {field('Strasse & Nr.', 'dbStreet')}
                {field('PLZ', 'dbZip')}
                {field('Ort', 'dbCity')}
              </div>
            </div>
          </div>

          {/* Vorschau + Download */}
          <div className="lg:sticky lg:top-24 h-fit">
            <div className="rounded-xl border border-gray-200 p-6 text-center">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Swiss QR Code Vorschau</h2>
              <div ref={qrWrapRef} className="relative inline-block" data-testid="qr-preview">
                {payload ? (
                  <>
                    <QRCodeCanvas value={payload} size={220} level="M" includeMargin={false} />
                    {/* Schweizerkreuz-Overlay */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-9 w-9 bg-black flex items-center justify-center">
                      <div className="relative h-6 w-6">
                        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white" style={{ width: '6px', height: '20px' }} />
                        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white" style={{ width: '20px', height: '6px' }} />
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="h-[220px] w-[220px] bg-gray-50 border-2 border-dashed border-gray-200 rounded flex items-center justify-center text-sm text-gray-400 px-6">
                    Name, PLZ/Ort und gültige IBAN eingeben
                  </div>
                )}
              </div>
              <Button onClick={downloadPdf} disabled={!payload} className="w-full mt-6 bg-emerald-600 hover:bg-emerald-700" data-testid="qr-download-pdf">
                <Download className="h-4 w-4 mr-2" /> QR-Rechnung als PDF herunterladen
              </Button>
              <p className="text-xs text-gray-400 mt-3">Zahlteil + Empfangsschein · A4 · keine Registrierung</p>
            </div>
          </div>
        </div>
      </section>

      {/* Erklärtext */}
      <section className="py-14 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 prose-sm">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Die QR-Rechnung: Der Schweizer Standard seit 2022</h2>
          <p className="text-gray-600 mb-4">
            Seit dem 1. Oktober 2022 ersetzt die QR-Rechnung die roten und orangen Einzahlungsscheine vollständig.
            Herzstück ist der Swiss QR Code nach ISO 20022 mit dem Schweizerkreuz im Zentrum: Er enthält alle
            Zahlungsinformationen maschinenlesbar — IBAN, Empfänger, Betrag, Währung und Referenz. Der Rechnungsempfänger
            scannt den Code mit der Banking-App oder zahlt am Schalter mit dem Empfangsschein.
          </p>
          <p className="text-gray-600 mb-4">
            Für Selbständigerwerbende gilt: Mit QR-IBAN und 27-stelliger QR-Referenz lassen sich Zahlungseingänge
            automatisch abgleichen — jede Rechnung bekommt ihre eigene Referenznummer. Wer keine QR-IBAN hat,
            nutzt die normale IBAN mit Creditor Reference (RF…) oder ganz ohne Referenz.
          </p>
          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-10" id="mwst">Rechnung schreiben in der Schweiz: MWST-Sätze und Pflichtangaben</h2>
          <p className="text-gray-600 mb-4">
            Seit 1. Januar 2024 gelten <strong>8,1 % Normalsatz</strong>, <strong>2,6 % reduzierter Satz</strong> (Lebensmittel, Bücher, Medikamente)
            und <strong>3,8 % Sondersatz</strong> für Beherbergung. MWST-pflichtig wirst du ab CHF 100'000 steuerbarem Jahresumsatz
            (Art. 10 MWSTG); darunter stellst du Rechnungen ohne Steuerausweis. Eine vollständige Rechnung nennt nach Art. 26 MWSTG
            Leistungserbringer mit UID (Zusatz „MWST“), Empfänger, Datum, Leistung, Entgelt und Steuersatz. Aufbewahrungsfrist: 10 Jahre (Art. 958f OR).
          </p>
          <p className="text-gray-600">
            Wer regelmässig Rechnungen mit Positionen, MWST-Ausweis und Kundenverwaltung stellt, nutzt{' '}
            <Link to="/signup" className="text-emerald-700 font-medium underline decoration-emerald-300 underline-offset-4">ivoliq kostenlos</Link>{' '}
            — mit QR-Zahlteil auf jeder Rechnung, Zeiterfassung und{' '}
            <Link to="/steuerruecklagen-freelancer-schweiz" className="text-emerald-700 font-medium underline decoration-emerald-300 underline-offset-4">Steuer-Rücklage für die Schweiz</Link>.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14" data-testid="qr-faq">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Häufige Fragen zur QR-Rechnung</h2>
          <div className="space-y-3">
            {FAQ.map((f, i) => (
              <div key={i} className="border border-gray-200 rounded-lg overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full px-5 py-4 text-left flex justify-between items-center gap-3" data-testid={`qr-faq-${i}`}>
                  <span className="font-semibold text-gray-900">{f.q}</span>
                  <ChevronDown className={`h-4 w-4 text-emerald-600 flex-shrink-0 ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && <div className="px-5 pb-4 text-gray-600 text-sm leading-relaxed">{f.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
